// What the chrome is covering, as numbers. **Zero imports, and that is the
// rule** — the same one `layout.ts` states and for the same reason: half the
// UI needs these numbers and the other half is imported *by* the shell that
// lays them out, so reading them off a component makes a cycle whose symptom
// is a blank page and "cannot access HEADER_HEIGHT before initialization",
// which says nothing about layout. The React half lives in
// `InsetRegistry.tsx` and holds no arithmetic; this file holds no React.
//
// **This is the only geometry source** (PLAN-UI §28.1, §28.5). It succeeded
// `layout.ts` at U8, which could compute the answers from two arguments because
// the geometry was fixed — a 56px header row and a dock of a known width on a
// known side. There is no dock to measure now: readouts, turn row, hotbar, tool
// rail, hand and inspector are separate overlays hugging separate edges, each
// sized by its own contents. So every floating piece *registers the rect it
// occupies* and one consumer folds them into the three answers everything
// downstream needs: per-edge insets for `setViewInset`/`fitView`, the free-area
// rect a hint card may be placed in, and the clip-path board-anchored overlays
// may paint inside.
//
// **`edge: null` is the load-bearing case.** A piece floating mid-board — the
// inspector, at the selection — registers so it can be reasoned about, and
// contributes *nothing* to the insets: the camera must not move because
// something was selected. That is §28.3's standing rule ("nothing about a
// selection reflows anything"), and it is the reason an inset is tagged with an
// edge rather than derived from where a rect happens to sit.
//
// The fold is per edge and order-independent, so nothing here is stateful and
// the registry may notify in any order.

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type Edge = "left" | "right" | "top" | "bottom";

/** One floating piece of chrome: what it covers, and which edge it hugs. */
export interface ChromeRect {
  id: string;
  rect: Rect;
  /** `null` — floats over the board, covers nothing the camera should avoid. */
  edge: Edge | null;
}

export interface EdgeInsets {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface Viewport {
  width: number;
  height: number;
}

export const NO_INSETS: EdgeInsets = { left: 0, right: 0, top: 0, bottom: 0 };

/**
 * An unmounted piece must not leave a ghost inset.
 *
 * A component that has rendered but not laid out measures 0×0, and a rect of no
 * size still has an `x` and a `y` — so a zero-height overlay pinned to the
 * bottom would otherwise report the whole bottom edge as covered. Measured
 * emptiness means "not there", never "there, at the origin".
 */
const isEmpty = (rect: Rect): boolean => rect.width <= 0 || rect.height <= 0;

/**
 * How far in from each edge the chrome reaches.
 *
 * Per edge, the *deepest* piece tagged with it — two overlays on one edge do not
 * add up, they overlap, and the camera wants the one that reaches furthest in.
 * Independent per edge, so this is a pure fold with no ordering dependence.
 */
export const edgeInsets = (
  entries: readonly ChromeRect[],
  viewport: Viewport
): EdgeInsets => {
  const insets = { ...NO_INSETS };
  for (const entry of entries) {
    if (entry.edge === null || isEmpty(entry.rect)) continue;
    const { x, y, width, height } = entry.rect;
    // Depth from the edge it hugs, never from the edge it happens to be nearest:
    // the tag is the claim, and a piece dragged off its edge is still its piece.
    const depth =
      entry.edge === "left"
        ? x + width
        : entry.edge === "right"
          ? viewport.width - x
          : entry.edge === "top"
            ? y + height
            : viewport.height - y;
    // A rect off the viewport, or one measured mid-transition as it slides in
    // from outside, gives a negative depth — beaten by the 0 the fold starts
    // from, so it covers nothing.
    if (depth > insets[entry.edge]) insets[entry.edge] = depth;
  }
  return insets;
};

/**
 * The rectangle nothing is covering — where something may be **placed**.
 *
 * The same subtraction `overlayClipOf` makes, as numbers rather than as a
 * `clip-path`, and both are needed: a clip cuts *painting* and says nothing
 * about placement. The hint card may not be clipped — it has to stay readable
 * wherever its target is — so it is the one overlay that is given the area and
 * told to stay in it.
 */
export const freeAreaOf = (
  entries: readonly ChromeRect[],
  viewport: Viewport
): Rect => {
  const { left, right, top, bottom } = edgeInsets(entries, viewport);
  return {
    x: left,
    y: top,
    width: Math.max(0, viewport.width - left - right),
    height: Math.max(0, viewport.height - top - bottom)
  };
};

/**
 * Paint over the board, and never under the chrome.
 *
 * Board-anchored overlays are `position: fixed` across the viewport because they
 * place their children at projected *viewport* coordinates, so they cannot be
 * sized to the free area without offsetting every child. `clip-path` cuts the
 * painting without touching the coordinate space, which is exactly the
 * distinction needed: a rank label for a square behind the hotbar draws itself
 * neatly on top of it otherwise.
 */
export const overlayClipOf = (
  entries: readonly ChromeRect[],
  viewport: Viewport
): string => {
  const { left, right, top, bottom } = edgeInsets(entries, viewport);
  return `inset(${top}px ${right}px ${bottom}px ${left}px)`;
};
